import { useCallback, useState } from "react";
import Cropper, { Area } from "react-easy-crop";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Loader2 } from "lucide-react";

interface Props {
  open: boolean;
  imageSrc: string | null;
  onCancel: () => void;
  onConfirm: (blob: Blob, previewUrl: string) => void;
}

type QualityPreset = "balanced" | "high" | "max";

const QUALITY_PRESETS: Record<
  QualityPreset,
  { label: string; size: number; quality: number; desc: string }
> = {
  balanced: { label: "Balanced", size: 384, quality: 0.82, desc: "Smallest file (~50 KB)" },
  high: { label: "High", size: 512, quality: 0.9, desc: "Recommended (~120 KB)" },
  max: { label: "Max", size: 768, quality: 0.94, desc: "Sharpest (~300 KB)" },
};

async function getCroppedBlob(
  imageSrc: string,
  crop: Area,
  preset: QualityPreset,
): Promise<{ blob: Blob; url: string; bytes: number }> {
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = imageSrc;
  });
  const { size: target, quality } = QUALITY_PRESETS[preset];
  // Never upscale beyond source crop — keeps result crisp, avoids blur
  const size = Math.min(target, Math.floor(crop.width));
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, size, size);
  return await new Promise((resolve) => {
    canvas.toBlob(
      (b) => {
        const blob = b!;
        resolve({ blob, url: URL.createObjectURL(blob), bytes: blob.size });
      },
      "image/jpeg",
      quality,
    );
  });
}

function fmtKB(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function AvatarCropperDialog({ open, imageSrc, onCancel, onConfirm }: Props) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState<Area | null>(null);
  const [quality, setQuality] = useState<QualityPreset>("high");
  const [busy, setBusy] = useState(false);
  const [estBytes, setEstBytes] = useState<number | null>(null);

  const onComplete = useCallback((_: Area, pixels: Area) => setArea(pixels), []);

  const handleConfirm = async () => {
    if (!imageSrc || !area) return;
    setBusy(true);
    try {
      const { blob, url, bytes } = await getCroppedBlob(imageSrc, area, quality);
      setEstBytes(bytes);
      onConfirm(blob, url);
    } finally {
      setBusy(false);
    }
  };

  const preset = QUALITY_PRESETS[quality];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Crop your photo</DialogTitle>
        </DialogHeader>
        <div className="relative h-72 w-full overflow-hidden rounded-md bg-muted">
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onComplete}
            />
          )}
        </div>
        <div className="space-y-2 px-1">
          <Label htmlFor="cropper-zoom" className="text-xs text-muted-foreground">
            Zoom
          </Label>
          <Slider
            id="cropper-zoom"
            value={[zoom]}
            min={1}
            max={3}
            step={0.01}
            onValueChange={(v) => setZoom(v[0])}
            aria-label="Image zoom"
            aria-valuetext={`${zoom.toFixed(2)} times`}
          />
        </div>
        <div className="space-y-2 px-1">
          <div className="flex items-center justify-between">
            <span id="cropper-quality-label" className="text-xs text-muted-foreground">
              Image quality
            </span>
            <span
              id="cropper-quality-desc"
              className="text-[11px] text-muted-foreground"
              aria-live="polite"
            >
              {preset.size}px · {preset.desc}
              {estBytes !== null && ` · last: ${fmtKB(estBytes)}`}
            </span>
          </div>
          <ToggleGroup
            type="single"
            value={quality}
            onValueChange={(v) => v && setQuality(v as QualityPreset)}
            className="grid grid-cols-3 gap-1"
            aria-labelledby="cropper-quality-label"
            aria-describedby="cropper-quality-desc"
          >
            {(Object.keys(QUALITY_PRESETS) as QualityPreset[]).map((k) => {
              const p = QUALITY_PRESETS[k];
              return (
                <ToggleGroupItem
                  key={k}
                  value={k}
                  className="text-xs focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  aria-label={`${p.label} quality, ${p.size} pixels, ${p.desc}`}
                >
                  {p.label}
                </ToggleGroupItem>
              );
            })}
          </ToggleGroup>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button onClick={handleConfirm} disabled={busy || !area}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Use photo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
